// Which emoji the thread offers, and why it is a short list. Lifted from the
// Andon app's `lib/emoji.ts`; the group headings are TR keys.
//
// NOT THE WHOLE UNICODE SET, DELIBERATELY.
//
// A full emoji keyboard is about 1 900 characters behind a search box, and a
// search box is a keyboard, and a keyboard on a phone with gloves on is the
// thing this app spends its whole design avoiding. The useful question here is
// not "which of two thousand" — it is "did you see it", "yes", "on my way",
// "that worked", and a couple of ways to be human about a bad morning.
//
// So: eight on the face of a message, about sixty behind a tap, grouped by what
// somebody is trying to SAY rather than by Unicode's own categories. Somebody
// who wants an emoji that is not here can still put one in the message with
// their own keyboard — the composer's text field is a text field.
//
// THE SERVER DOES NOT HAVE THIS LIST, and must not. `ReactBody` in the BFF's
// validate.ts checks the SHAPE of an emoji instead, so adding a face here is a
// one-line change to a picker rather than a change to a contract; a shared
// allow-list would drift the first time somebody edited one copy.

import type { StringKey } from '@/i18n/strings';

/**
 * The eight offered without opening anything.
 *
 * Ordered by how often a factory thread needs them, not by sentiment: the top
 * three are acknowledgements, which is what a reaction is mostly FOR here —
 * "seen", "done", "thanks" said without adding a line to a thread somebody is
 * reading at a stopped machine.
 */
export const QUICK_REACTIONS = ['👍', '✅', '🙏', '👀', '🎉', '❤️', '😂', '😮'] as const;

export interface EmojiGroup {
  label: StringKey;
  emoji: readonly string[];
}

/** The picker, grouped by what somebody is trying to say. */
export const EMOJI_GROUPS: readonly EmojiGroup[] = [
  { label: 'emojiSayingYesT', emoji: ['👍', '✅', '🙏', '👀', '💪', '🤝', '👌', '🫡', '💯', '🔥'] },
  { label: 'emojiSayingNoT', emoji: ['👎', '❌', '🛑', '⚠️', '🚧', '🤔', '😬', '🤷'] },
  { label: 'emojiHowItIsGoingT', emoji: ['🎉', '😂', '😅', '😮', '😢', '😡', '🥲', '😴', '🫠', '🙃'] },
  { label: 'emojiTheWorkT', emoji: ['🔧', '🔩', '🪛', '⚙️', '🧰', '🪚', '📦', '📐', '🧯', '🔌'] },
  { label: 'emojiTheClockT', emoji: ['⏱️', '⌛', '🕐', '📅', '🚀', '🐌'] },
  { label: 'emojiOddsAndEndsT', emoji: ['❤️', '☕', '🍕', '🌧️', '☀️', '🧊', '🔊', '📸'] },
];

/** Every emoji the picker offers, flat — for tests and for a sanity check. */
export const ALL_EMOJI: readonly string[] = EMOJI_GROUPS.flatMap((g) => g.emoji);

/**
 * Insert an emoji into a draft at the caret.
 *
 * Returns the new text and where the caret should land, because a caret left
 * at the end is how somebody typing "the rack is " in the middle of a sentence
 * ends up with the face at the wrong end of it. Same contract as
 * `applyMention` in lib/mentions.ts, and for the same reason.
 *
 * No space is added. An emoji is punctuation as often as it is a word — "on my
 * way 👍" wants one and "👍👍" does not — and a space nobody asked for is a
 * character somebody has to delete on a phone.
 */
export function insertEmoji(text: string, caret: number, emoji: string): { text: string; caret: number } {
  const at = Math.max(0, Math.min(caret, text.length));
  return { text: `${text.slice(0, at)}${emoji}${text.slice(at)}`, caret: at + emoji.length };
}
